export type DeepPartial<T> = T extends
  | string
  | number
  | bigint
  | boolean
  | null
  | undefined
  | symbol
  | Date
  ? T
  : // eslint-disable-next-line @typescript-eslint/ban-types
  T extends Function
  ? T
  : T extends Array<infer ArrayType>
  ? Array<ArrayType>
  : T extends ReadonlyArray<infer ArrayType>
  ? ReadonlyArray<ArrayType>
  : T extends Set<infer SetType>
  ? Set<DeepPartial<SetType>>
  : T extends ReadonlySet<infer SetType>
  ? ReadonlySet<SetType>
  : T extends Map<infer KeyType, infer ValueType>
  ? Map<DeepPartial<KeyType>, DeepPartial<ValueType>>
  : T extends ReadonlyMap<infer KeyType, infer ValueType>
  ? ReadonlyMap<DeepPartial<KeyType>, DeepPartial<ValueType>>
  : {
      [K in keyof T]?: DeepPartial<T[K]>;
    };

function shouldDeepMerge(obj: unknown) {
  return !(
    Array.isArray(obj) ||
    obj instanceof Set ||
    obj instanceof Map ||
    obj instanceof WeakSet ||
    obj instanceof WeakMap ||
    obj instanceof Date ||
    obj instanceof Error ||
    obj === null ||
    typeof obj !== 'object'
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function deepMerge<TObject extends Record<string, any>>(
  defaultObject: TObject,
  ...extendedObjects: NoInfer<Array<DeepPartial<TObject> | undefined>>
): TObject {
  const destination: Record<string, unknown> = {};

  Object.keys(defaultObject || {}).forEach((key) => {
    const field = defaultObject[key];

    if (shouldDeepMerge(field)) {
      destination[key] = deepMerge(defaultObject[key], {});
    } else {
      destination[key] = field;
    }
  });

  // eslint-disable-next-line no-restricted-syntax
  for (const extendedObject of extendedObjects) {
    Object.keys(extendedObject || {}).forEach((key) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeepPartial<TObject>'.
      const field = extendedObject?.[key];

      if (field === undefined) {
        return;
      }

      if (shouldDeepMerge(destination[key]) && shouldDeepMerge(field)) {
        destination[key] = deepMerge(
          destination[key] as Record<string, any>,
          field,
        );
      } else {
        destination[key] = field;
      }
    });
  }

  return destination as TObject;
}
